import { fetchAuthSession } from 'aws-amplify/auth';
import { isNetworkError } from 'utils/networkErrorUtils';

class TokenRefreshManager {
  constructor() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.lastRefreshTime = 0;
    this.minRefreshInterval = 2 * 60 * 1000;
    this.refreshQueue = [];
  }

  async refreshToken(forceRefresh = false) {
    if (this.isRefreshing && !forceRefresh) {
      return this.refreshPromise;
    }

    const now = Date.now();
    if (!forceRefresh && (now - this.lastRefreshTime) < this.minRefreshInterval) {
      console.log('[TokenRefresh] Skipping refresh - too soon since last refresh');
      return this.getCurrentSession();
    }

    if (!forceRefresh) {
      try {
        const currentSession = await this.getCurrentSession();
        if (!this.isTokenExpired(currentSession)) {
          console.log('[TokenRefresh] Token not expired, skipping refresh');
          return currentSession;
        }
      } catch (error) {
        console.log('[TokenRefresh] Could not check current session, proceeding with refresh');
      }
    }

    this.isRefreshing = true;
    this.lastRefreshTime = now;

    this.refreshPromise = this.performRefresh();
    
    try {
      const result = await this.refreshPromise;
      this.processQueue(null, result);
      return result;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('[TokenRefresh] Network error during refresh, attempting to use cached session');
        try {
          const cachedSession = await this.getCurrentSession();
          if (cachedSession?.tokens?.idToken && !this.isTokenExpired(cachedSession)) {
            console.log('[TokenRefresh] Using cached valid session due to network error');
            this.processQueue(null, cachedSession);
            return cachedSession;
          }
        } catch (cacheError) {
          console.error('[TokenRefresh] Failed to get cached session:', cacheError);
        }
      }
      this.processQueue(error, null);
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  async performRefresh() {
    try {
      console.log('[TokenRefresh] Starting token refresh...');
      const session = await fetchAuthSession({ forceRefresh: true });
      
      if (session.tokens?.idToken) {
        console.log('[TokenRefresh] Token refreshed successfully:', session.tokens.idToken.payload.sub);
        return session;
      } else {
        throw new Error('No valid tokens received');
      }
    } catch (error) {
      console.error('[TokenRefresh] Token refresh failed:', error);
      throw error;
    }
  }

  async getCurrentSession() {
    try {
      return await fetchAuthSession();
    } catch (error) {
      console.error('[TokenRefresh] Failed to get current session:', error);
      throw error;
    }
  }

  async getValidSession() {
    try {
      const session = await this.getCurrentSession();
      
      if (this.isTokenExpired(session)) {
        console.log('[TokenRefresh] Token expired, refreshing...');
        try {
          return await this.refreshToken(true);
        } catch (error) {
          if (isNetworkError(error)) {
            console.warn('[TokenRefresh] Network error during refresh in getValidSession, using cached session if available');
            if (session?.tokens?.idToken) {
              console.log('[TokenRefresh] Returning cached session despite network error');
              return session;
            }
          }
          throw error;
        }
      }
      
      return session;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('[TokenRefresh] Network error getting session, this may be temporary');
      }
      console.error('[TokenRefresh] Failed to get valid session:', error);
      throw error;
    }
  }

  isTokenExpired(session) {
    if (!session.tokens?.idToken) {
      return true;
    }

    const token = session.tokens.idToken;
    const expirationTime = token.payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = 2 * 60 * 1000; // 2 minutes buffer before actual expiration

    const timeUntilExpiration = expirationTime - currentTime;
    const isExpired = timeUntilExpiration < bufferTime;
    
    if (isExpired) {
      console.log(`[TokenRefresh] Token expires in ${Math.round(timeUntilExpiration / 1000)} seconds`);
    } else {
      console.log(`[TokenRefresh] Token valid for ${Math.round(timeUntilExpiration / 60000)} more minutes`);
    }

    return isExpired;
  }

  processQueue(error, result) {
    this.refreshQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(result);
      }
    });
    this.refreshQueue = [];
  }

  reset() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.lastRefreshTime = 0;
    this.refreshQueue = [];
  }
}

export const tokenRefreshManager = new TokenRefreshManager();
